import { DOCUMENT } from '@angular/common';
import { Injectable, InjectionToken, inject } from '@angular/core';

import { Observable, of } from 'rxjs';

import { EnvironmentService } from '../environment/environment.service';
import { PlatformService } from '../platform/platform.service';

export const X_CSRF_TOKEN_HEADER_NAME = 'X-CSRF-Token';
export const AUTHORIZATION_HEADER_NAME = 'AUTHORIZATION';

export interface TokenStore {
  get ready$(): Observable<void>;
  get hasToken(): boolean;
  setToken(token: string): void;
}

export const TOKEN_STORE = new InjectionToken<TokenStore>('shared.util-core.TokenStore');

@Injectable({
  providedIn: 'root'
})
export class TokenService {
  private readonly env = inject(EnvironmentService);
  private readonly doc = inject(DOCUMENT);
  private readonly isBrowser = inject(PlatformService).isBrowser;
  private readonly tokenStore = inject(TOKEN_STORE, { optional: true });

  private localBearerToken: string;
  private localCsrfToken = this.getCsrfTokenFromDOM();

  get ready$(): Observable<void> {
    return this.tokenStore?.ready$ || of(null);
  }

  get bearerToken(): string {
    return this.isBrowser ? this.localBearerToken || this.env.bearerToken : null;
  }

  set bearerToken(token: string) {
    this.localBearerToken = token;
    this.tokenStore?.setToken(token);
  }

  get hasBearerToken(): boolean {
    return this.tokenStore?.hasToken || !!this.bearerToken;
  }

  get authToken(): string {
    const token = this.bearerToken || this.env.anonymousToken;
    return token && `${!this.bearerToken ? 'Basic' : 'Bearer'} ${token}`;
  }

  get authTokenHeader(): Record<string, string> {
    return { [AUTHORIZATION_HEADER_NAME]: this.authToken };
  }

  get csrfToken(): string {
    return this.localCsrfToken || '';
  }

  set csrfToken(val: string) {
    this.localCsrfToken = val;
  }

  get csrfTokenHeader(): Record<string, string> {
    return { [X_CSRF_TOKEN_HEADER_NAME]: this.csrfToken };
  }

  /**
   * Retrieve CSRF token from related meta tag in DOM, whenever app runs in AEM or Hybris context.
   * @private
   */
  private getCsrfTokenFromDOM(): string {
    const metaTag = this.isBrowser ? this.doc.querySelector("meta[name='csrf-token']") : null;
    return metaTag?.getAttribute('content');
  }
}
